import React from 'react'
import * as yup from 'yup'
import * as Request from '../../utilities/request'
import { Grade, GradeHistory } from '../../../../back-end/utilities/apiDefinitions'
import { Button, Col, Modal, Row } from 'react-bootstrap'
import { Formik, FormikErrors } from 'formik'
import { FormText } from '../ui/Form/Text'
import { FormSwitch } from '../ui/Form/Switch'
import { FormNumber } from '../ui/Form/Number'

import './addEditGradeModal.css'
import { DateTime } from 'luxon'
import { FormDate } from '../ui/Form/Date'
import { AuthState } from '../../App.d'
import { formatDateExcludeEpoch } from '../../utilities/format'

const defaultGradeHistory: Partial<GradeHistory> = {
	rate: 0,
	costRate: 0,
	effectiveDate: DateTime.now().plus({ days: 1 }).toISODate(),
}

const GradeValidationSchema = (isCostGradeRequired: boolean) => {
	yup.object().shape({
		name: yup.string().required('Grade name is required'),
		isActive: yup.boolean().required('Grade description is required'),
		gradeHistory: yup.array().of(
			yup.object().shape({
				rate: yup.number().required('Rate is required').positive('Rate must be positive'),
				costRate: isCostGradeRequired ? yup.number().required('Cost Rate is required').positive('Cost Rate must be positive') : yup.number().nullable(),
				effectiveDate: yup.date().required('Date is required'),
			})
		),
	})
}

interface AddEditGradeModalProps {
	authState: AuthState
	costRateIsRequired: boolean
	modalGrade: Grade | null
	setModalGrade: React.Dispatch<React.SetStateAction<Grade | null>>
	grades: Grade[]
	setGrades: React.Dispatch<React.SetStateAction<Grade[]>>
}

const AddEditGradeModal = (props: AddEditGradeModalProps) => {
	const [addingGradeHistory, setAddingGradeHistory] = React.useState(false)
	const [newGradeHistory, setNewGradeHistory] = React.useState<Partial<GradeHistory>>(defaultGradeHistory)

	if (props.modalGrade === null) {
		return null
	}

	const isNew = !props.modalGrade.gradeID

	const onHide = (isSubmitting: boolean) => {
		if (!isSubmitting) {
			props.setModalGrade(null)
		}
	}

	const onSubmit = async (values: Grade) => {
		if (values.gradeID) {
			const updateGradeRes = await Request.put(`grade?where=GradeID==${values.gradeID}`, values)
			if (updateGradeRes.data.success) {
				props.setGrades(props.grades.map((grade) => (grade.gradeID === values.gradeID ? updateGradeRes.data.grades[0] : grade)))
				props.setModalGrade(null)
			}
		} else {
			const newGradeRes = await Request.post('grade', values)
			if (newGradeRes.data.success) {
				props.setGrades([...props.grades, ...newGradeRes.data.grades])
				props.setModalGrade(null)
			}
		}
	}

	const onSubmitNewGradeHistory = async (
		setSubmitting: (isSubmitting: boolean) => void,
		setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<Grade>>
	) => {
		if (props.modalGrade === null) {
			return
		}
		setSubmitting(true)

		const newGradeRes = await Request.put(`grade/rate?where=GradeID==${props.modalGrade.gradeID}`, newGradeHistory)

		if (newGradeRes.data.success) {
			setFieldValue('gradeHistory', newGradeRes.data.grades[0].gradeHistory)
			props.setGrades(props.grades.map((grade) => (grade.gradeID === props.modalGrade?.gradeID ? newGradeRes.data.grades[0] : grade)))
			setNewGradeHistory(defaultGradeHistory)
			setAddingGradeHistory(false)
		}

		setSubmitting(false)
	}

	const labelCol = 4
	const labelJustify = 'end'

	return (
		<Formik initialValues={props.modalGrade} validationSchema={GradeValidationSchema(props.costRateIsRequired)} onSubmit={onSubmit}>
			{({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, setFieldTouched, setSubmitting }) => (
				<Modal show={!!props.modalGrade} onHide={() => onHide(isSubmitting)} dialogClassName="grade-modal">
					<Modal.Header>
						<h4 className="m-0">{isNew ? 'New Grade' : 'Edit Grade'}</h4>
					</Modal.Header>
					<Modal.Body className="grade-modal-body">
						<FormText
							name="name"
							label="Grade Name"
							labelCol={labelCol}
							labelJustify={labelJustify}
							disabled={addingGradeHistory}
							value={values.name}
							onChange={handleChange}
							onBlur={handleBlur}
							feedback={touched.name ? errors.name : ''}
							required
						/>
						<FormSwitch
							name="isActive"
							label="Active"
							labelCol={labelCol}
							labelJustify={labelJustify}
							disabled={addingGradeHistory}
							type="checkbox"
							checked={values.isActive}
							onChange={handleChange}
							onBlur={handleBlur}
							required
						/>
						{isNew ? (
							<>
								{props.costRateIsRequired ? (
									<FormNumber
										name="gradeHistory[0].costRate"
										label="Cost Rate"
										labelCol={labelCol}
										labelJustify={labelJustify}
										value={values.gradeHistory[0].costRate}
										onChange={(value) => setFieldValue('gradeHistory[0].costRate', value)}
										onBlur={() => setFieldTouched('gradeHistory[0].costRate', true)}
										min={0}
										max={1000000}
										feedback={
											touched.gradeHistory && touched.gradeHistory[0].costRate && errors.gradeHistory && errors.gradeHistory[0]
												? (errors.gradeHistory as FormikErrors<GradeHistory>[])[0].costRate
												: ''
										}
										required={props.costRateIsRequired}
									/>
								) : null}
								<FormNumber
									name="gradeHistory[0].rate"
									label="Sell Rate"
									labelCol={labelCol}
									labelJustify={labelJustify}
									value={values.gradeHistory[0].rate}
									onChange={(value) => setFieldValue('gradeHistory[0].rate', value)}
									onBlur={() => setFieldTouched('gradeHistory[0].rate', true)}
									min={0}
									max={1000000}
									feedback={
										touched.gradeHistory && touched.gradeHistory[0].rate && errors.gradeHistory && errors.gradeHistory[0]
											? (errors.gradeHistory as FormikErrors<GradeHistory>[])[0].rate
											: ''
									}
									required
								/>
							</>
						) : (
							<Row className="px-3">
								<Col className="grade-modal-body">
									<Row className="mini-table-header align-items-center py-2">
										{props.costRateIsRequired ? <Col sm={2}>Cost Rate($) / Hr</Col> : null}
										<Col sm={props.costRateIsRequired ? 2 : 4}>Sell Rate($) / Hr</Col>
										<Col sm={props.costRateIsRequired ? 3 : 4}>Effective Date</Col>
										<Col sm={4} className="center-flex">
											<Button
												variant="secondary"
												className="px-2 py-1"
												disabled={isSubmitting || addingGradeHistory}
												onClick={() => {
													setNewGradeHistory(defaultGradeHistory)
													setAddingGradeHistory(true)
												}}
											>
												Add
											</Button>
										</Col>
									</Row>
									{addingGradeHistory && (
										<Row className="align-items-center">
											{props.costRateIsRequired ? (
												<Col sm={2}>
													<FormNumber
														name="newGradeHistoryCostRate"
														value={newGradeHistory.costRate}
														onChange={(value) => setNewGradeHistory({ ...newGradeHistory, costRate: value })}
														min={0}
														max={1000000}
													/>
												</Col>
											) : null}
											<Col sm={props.costRateIsRequired ? 2 : 4}>
												<FormNumber
													name="newGradeHistoryRate"
													value={newGradeHistory.rate}
													onChange={(value) => setNewGradeHistory({ ...newGradeHistory, rate: value })}
													min={0}
													max={1000000}
												/>
											</Col>
											<Col sm={props.costRateIsRequired ? 3 : 4}>
												<FormDate
													name="newGradeHistoryEffectiveDate"
													value={newGradeHistory.effectiveDate}
													onChange={(e) => setNewGradeHistory({ ...newGradeHistory, effectiveDate: e.target.value })}
													min={DateTime.now().plus({ days: 1 }).toISODate()}
												/>
											</Col>
											<Col sm={2} className="center-flex">
												<Button
													variant="secondary"
													className="p-2"
													disabled={isSubmitting}
													onClick={() => setAddingGradeHistory(false)}
												>
													Cancel
												</Button>
											</Col>
											<Col sm={2} className="center-flex">
												<Button
													variant="primary"
													className="p-2"
													disabled={isSubmitting}
													onClick={() => onSubmitNewGradeHistory(setSubmitting, setFieldValue)}
												>
													Add
												</Button>
											</Col>
										</Row>
									)}

									{values.gradeHistory
										.sort((a, b) => (a.effectiveDate > b.effectiveDate ? -1 : 1))
										.map((gradeHistory, index) => (
											<Row key={index}>
												{props.costRateIsRequired ? <Col sm={2}>${gradeHistory.costRate}</Col> : null}
												<Col sm={props.costRateIsRequired ? 2 : 4}>${gradeHistory.rate}</Col>
												<Col sm={props.costRateIsRequired ? 3 : 4}>{formatDateExcludeEpoch(gradeHistory.effectiveDate)}</Col>
												<Col />
											</Row>
										))}
								</Col>
							</Row>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Row>
							<Col />
							<Col sm="auto">
								<Button variant="secondary" className="p-2" disabled={isSubmitting || addingGradeHistory} onClick={() => onHide(isSubmitting)}>
									Cancel
								</Button>
							</Col>
							<Col sm="auto" className="p-0">
								<Button variant="primary" className="px-3 py-2" disabled={isSubmitting || addingGradeHistory} onClick={() => handleSubmit()}>
									{isNew ? 'Add' : 'Save'}
								</Button>
							</Col>
						</Row>
					</Modal.Footer>
				</Modal>
			)}
		</Formik>
	)
}

export default AddEditGradeModal
